import type { TaskMaterialsCheckDto } from './demands';
import type { FleetBlockDto } from './fleet';
import { z } from 'zod';
import { type AbsenceStatus, type AbsenceType, type ApprovalDecision, type AssignmentState, type ConflictSeverity, type EntryKind, LEAVE_KIND_KEYS, type LeaveKind, type MembershipKind, type TaskStatus, type TaskType } from './planning';
import './schemas';

const str = (max: number) => z.string().trim().max(max);
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data w formacie RRRR-MM-DD');
const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Godzina w formacie GG:MM');
const uuid = z.string().uuid();

export const teamSaveSchema = z.object({
  name: str(80).min(2, 'Podaj nazwę ekipy'),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  leaderId: uuid.nullable().default(null),
  tradeCodes: z.array(z.string().max(4)).max(20).default([]),
  active: z.boolean().default(true),
});

export const membershipCreateSchema = z
  .object({
    userId: uuid,
    kind: z.enum(['stale', 'czasowe']),
    validFrom: date,
    validTo: date.nullable().default(null),
    note: str(300).default(''),
  })
  .refine((v) => !v.validTo || v.validTo >= v.validFrom, { message: 'Data „do” nie może być wcześniejsza niż „od”', path: ['validTo'] })
  .refine((v) => v.kind !== 'czasowe' || !!v.validTo, { message: 'Czasowe przejście wymaga daty „do”', path: ['validTo'] });

export const membershipEndSchema = z.object({ validTo: date, note: str(300).default('') });

/** Przeniesienie stałego członkostwa do innej ekipy od wskazanego dnia (domyślnie od dziś). */
export const membershipMoveSchema = z.object({ teamId: uuid, date, note: str(300).default('') });

export const taskCreateSchema = z.object({
  taskType: z.enum(['wizja', 'pomiary', 'montaz', 'uruchomienie', 'serwis', 'przeglad', 'inne', 'poprawka']),
  caseId: uuid,
  scopeIds: z.array(uuid).default([]),
  title: str(200).default(''),
  description: str(4000).default(''),
});

/** „Zaplanuj realizację”: wybrane branże sprawy — jedno wspólne zadanie albo osobne zadanie dla każdej. */
export const realizationCreateSchema = z.object({
  scopeIds: z.array(uuid).min(1, 'Wybierz co najmniej jedną branżę'),
  separate: z.boolean().default(false),
});

/** Branża (zakres) sprawy do realizacji — po umowie; z aktywnym zadaniem realizacji, jeśli już jest. */
export interface RealizationCandidateDto {
  scopeId: string;
  tradeCode: string;
  tradeName: string;
  companyId: string;
  contractId: string;
  contractNumber: string | null;
  /** branża poza branżami osoby (brak funkcji koordynatora i przypisanego zadania): kto ma zadanie planowania ('' = nikt); null — dostępna */
  blockedBy: string | null;
  conditionsMet: boolean;
  conditionsText: string;
  task: { id: string; number: string; status: string; dateFrom: string | null } | null;
  /** prace z podpisanego aneksu czekają na zaplanowanie (praca umowy już wykonana) — numer aneksu */
  annexNumber?: string | null;
}

/** Aneks wydany do podpisu, jeszcze niepodpisany — znacznik w oknach planowania (P35, bez blokady). */
export interface PendingAnnexDto {
  id: string;
  number: string | null;
  issuedAt: string | null;
  /** zakresy umowy głównej, której dotyczy aneks */
  scopeIds: string[];
}

/** QA qa10301331 P11: zaplanowana / trwająca praca zakresu aneksu (ostrzeżenie w oknie aneksu). */
export interface AnnexPlannedWorkDto {
  taskId: string;
  number: string;
  taskType: TaskType;
  label: string;
  status: string;
  dateFrom: string | null;
  tradeName: string;
}

const timeRange = <T extends { timeFrom: string | null; timeTo: string | null; dateFrom: string; dateTo: string }>(s: z.ZodType<T>) =>
  s
    .refine((v) => v.dateTo >= v.dateFrom, { message: 'Data „do” nie może być wcześniejsza niż „od”', path: ['dateTo'] })
    .refine((v) => (v.timeFrom === null) === (v.timeTo === null), { message: 'Podaj obie godziny albo żadną (cały dzień)', path: ['timeTo'] })
    .refine((v) => !v.timeFrom || !v.timeTo || v.timeTo > v.timeFrom, { message: 'Godzina „do” musi być późniejsza niż „od”', path: ['timeTo'] });

/**
 * Wpis planu (odcinek pracy): ekipa i/lub osoby w dniach od–do, cały dzień albo godziny. Zadanie może mieć
 * kilka wpisów z przerwami i różnymi ekipami (np. montaż pn–wt ekipa A, pt ekipa A, kolejny tydzień ekipa B).
 */
export const taskSegmentSchema = timeRange(
  z.object({
    /** istniejący wpis (zmiana) — bez identyfikatora: nowy wpis */
    id: uuid.optional(),
    dateFrom: date,
    dateTo: date,
    timeFrom: time.nullable().default(null),
    timeTo: time.nullable().default(null),
    skipWeekends: z.boolean().default(true),
    teamId: uuid.nullable().default(null),
    added: z.array(uuid).max(30).default([]),
    excluded: z.array(uuid).max(30).default([]),
  }),
).refine((v) => v.teamId || v.added.length, { message: 'Wybierz ekipę albo osoby', path: ['teamId'] });

const SEGMENT_KEYS = ['dateFrom', 'dateTo', 'timeFrom', 'timeTo', 'skipWeekends', 'teamId', 'added', 'excluded'] as const;

/** Zapis terminu: lista wpisów; dawny format (jeden termin w polach głównych) = jeden wpis. */
export const taskPlanSchema = z.preprocess(
  (v) => {
    if (!v || typeof v !== 'object' || Array.isArray(v) || 'segments' in v) return v;
    const o = v as Record<string, unknown>;
    return { segments: [Object.fromEntries(SEGMENT_KEYS.filter((k) => k in o).map((k) => [k, o[k]]))], force: o.force, forceReason: o.forceReason, notifyClient: o.notifyClient, rescheduleReason: o.rescheduleReason, resolveBlocker: o.resolveBlocker, materialsAck: o.materialsAck };
  },
  z.object({
    segments: z.array(taskSegmentSchema).min(1, 'Dodaj co najmniej jeden termin').max(20),
    /** zapis mimo konfliktu (uprawnienie planowanie.konflikt) */
    force: z.boolean().default(false),
    forceReason: str(500).default(''),
    /** U3: e-mail do klienta z terminem (data, godziny, branża, kontakt) — także ponownie po przełożeniu */
    notifyClient: z.boolean().default(false),
    /** QA qa10302046 P1: powód przełożenia — wymagany (API), gdy zadanie miało termin, a dni lub godziny się zmieniają */
    rescheduleReason: str(500).default(''),
    /**
     * QA qa10311123 3.5: zadanie wstrzymane blokadą — opis rozwiązania; niepusty: razem z planem aktywne blokady zostają
     * rozwiązane, a zadanie wznowione (pusty — nowy termin nie wznawia zadania)
     */
    resolveBlocker: str(500).default(''),
    /** świadome „Zaplanuj mimo braków” materiałowych (wpis w historii) */
    materialsAck: z.boolean().default(false),
  }),
);

/** QA qa10302046 P1: termin (dni i godziny wpisów) do porównania przy przełożeniu — bez ekipy i składu. */
export function planTermKey(segments: { dateFrom: string; dateTo: string; timeFrom: string | null; timeTo: string | null }[]): string {
  return segments
    .map((s) => `${s.dateFrom}|${s.dateTo}|${(s.timeFrom ?? '').slice(0, 5)}|${(s.timeTo ?? '').slice(0, 5)}`)
    .sort()
    .join(';');
}

export const taskCancelSchema = z.object({ reason: str(500).min(3, 'Podaj powód') });

/** Nieobecność: osoba zastępująca i powiadomienie klientów otwartych spraw o osobie kontaktowej (dawne okno „Zastępstwa”). */
const absenceDeputyFields = {
  /** null — bez zastępstwa */
  deputyId: uuid.nullable().default(null),
  /** e-mail do klientów otwartych spraw nieobecnego z danymi zastępcy — wysyłany, gdy zastępstwo zaczyna obowiązywać (zatwierdzona nieobecność) */
  notifyClients: z.boolean().default(false),
  /** tylko te sprawy (spośród otwartych spraw nieobecnego); brak — wszystkie */
  notifyCaseIds: z.array(uuid).max(1000).optional(),
};

export const entryCreateSchema = timeRange(
  z.object({
    kind: z.enum(['nieobecnosc', 'wydarzenie', 'wpis']),
    title: str(200).default(''),
    absenceType: z.enum(['urlop', 'l4', 'szkolenie', 'inne']).nullable().default(null),
    /** urlop: rodzaj (wypoczynkowy, na żądanie…) — tylko informacja dla zatwierdzających */
    leaveKind: z.enum(LEAVE_KIND_KEYS).nullable().default(null),
    /** nieobecność: osoba (domyślnie zgłaszający); wydarzenie/wpis: uczestnicy */
    userIds: z.array(uuid).max(200).default([]),
    teamIds: z.array(uuid).max(50).default([]),
    allUsers: z.boolean().default(false),
    dateFrom: date,
    dateTo: date,
    timeFrom: time.nullable().default(null),
    timeTo: time.nullable().default(null),
    note: str(1000).default(''),
    /** wydarzenie: uczestnicy potwierdzają obecność */
    attendanceRequired: z.boolean().default(false),
    ...absenceDeputyFields,
  }),
);

/**
 * Zmiana nieobecności (docs/16 — zastępstwo): daty (zatwierdzający; pracownik — wniosek przed pierwszą decyzją)
 * i osoba zastępująca. Zastępstwo idzie za datami nieobecności.
 */
export const absenceUpdateSchema = z
  .object({ dateFrom: date, dateTo: date, ...absenceDeputyFields })
  .refine((v) => v.dateFrom <= v.dateTo, { message: 'Data końca przed datą początku', path: ['dateTo'] });
export type AbsenceUpdateInput = z.infer<typeof absenceUpdateSchema>;

/** Operator: obecność uczestnika (null — usunięcie potwierdzenia). */
export const attendanceSetSchema = z.object({ present: z.boolean().nullable() });

/** Decyzja o wniosku; odmowa wymaga powodu (pracownik widzi go w aplikacji). */
export const entryDecisionSchema = z
  .object({ decision: z.enum(['zatwierdzona', 'odrzucona']), note: str(500).default('') })
  .refine((v) => v.decision !== 'odrzucona' || v.note.length >= 3, { message: 'Podaj powód odmowy', path: ['note'] });

/** Drabinka akceptacji wniosków pracownika: kolejne osoby zatwierdzające (pusta — osoby z uprawnieniem). */
export const absenceLadderSchema = z.object({
  approverIds: z
    .array(uuid)
    .max(6, 'Najwyżej 6 poziomów akceptacji')
    .refine((a) => new Set(a).size === a.length, 'Ta sama osoba nie może być na dwóch poziomach'),
});
export type AbsenceLadderInput = z.infer<typeof absenceLadderSchema>;

export type TaskPlanInput = z.infer<typeof taskPlanSchema>;
export type TaskSegmentInput = z.infer<typeof taskSegmentSchema>;
export type EntryCreateInput = z.infer<typeof entryCreateSchema>;

// ── DTO ────────────────────────────────────────────────────────────────────

export interface PersonDto {
  id: string;
  fullName: string;
  /** pracuje w terenie: bez konta albo bez dostępu do panelu */
  isField: boolean;
  isSubcontractor: boolean;
  /** branże osoby (funkcja „monter”; w ekipie — branże ekipy) */
  tradeCodes: string[];
}

export interface TeamMemberDto {
  membershipId: string;
  userId: string;
  fullName: string;
  kind: MembershipKind;
  validFrom: string;
  validTo: string | null;
  note: string;
}

export interface TeamDto {
  id: string;
  name: string;
  color: string;
  leaderId: string | null;
  tradeCodes: string[];
  active: boolean;
  members: TeamMemberDto[];
}

export interface ConflictDto {
  userId: string;
  personName: string;
  day: string;
  severity: ConflictSeverity;
  text: string;
  withKind: string;
  withId: string;
}

export interface TaskDto {
  id: string;
  number: string;
  taskType: TaskType;
  status: TaskStatus;
  title: string;
  description: string;
  caseId: string | null;
  caseNumber: string | null;
  /** zgłoszenie serwisowe (zadania przeglądu / serwisu, P6) */
  ticketId: string | null;
  ticketNumber: string | null;
  clientName: string | null;
  siteAddress: string | null;
  scopes: { id: string; tradeCode: string; companyId: string }[];
  /** praca wstrzymana: cała sprawa albo wszystkie zakresy pracy wstrzymane — nie do zaplanowania, dopóki trwa (docs/10 §16) */
  onHold: { what: 'sprawa' | 'zakres'; reason: string; until: string | null } | null;
  dateFrom: string | null;
  dateTo: string | null;
  timeFrom: string | null;
  timeTo: string | null;
  skipWeekends: boolean;
  teamId: string | null;
  added: string[];
  excluded: string[];
  /** zaplanowane przed spełnieniem warunków startu — instalator nie może rozpocząć */
  conditional: boolean;
  conditionsMet: boolean;
  /** QA qa10301331 P8: chwila spełnienia warunków startu (podpis umowy / pełna zaliczka) — tylko karta zadania (GET /tasks/:id) */
  startReadyAt?: string | null;
  /** QA qa10301331 P11: aneksy wydane po zaplanowaniu pracy do jej zakresów (jeszcze niepodpisane albo podpisane po planie) — karta zadania */
  annexAfterPlan?: {
    id: string;
    number: string | null;
    status: string;
    issuedAt: string | null;
    /** QA P9: wydany po zaplanowaniu pracy (false — wydany wcześniej, czeka na podpis); starsze serwery nie wysyłają */
    issuedAfterPlan?: boolean;
    /** QA P2: chwila odnotowania podpisu (podpisany po zaplanowaniu) */
    signedAt?: string | null;
  }[];
  /** wpisy planu (odcinki pracy) — pola termin / ekipa powyżej to podsumowanie (od pierwszego do ostatniego dnia) */
  segments: TaskSegmentDto[];
  assignments: { day: string; userId: string; source: 'ekipa' | 'recznie'; state: AssignmentState }[];
  conflicts: ConflictDto[];
  /** Flota (docs/19 §6): auto ekipy / osoby niedostępne w dniach zadania (od dziś) — informacja, bez blokady; brak — moduł wyłączony albo bez dostępności floty */
  fleetWarnings?: string[];
  cancelReason: string;
  plannedByName: string | null;
  can: { plan: boolean; cancel: boolean };
  /** U3: powiadomienie klienta o terminie — adres klienta i ostatnia wysłana informacja (null — zadanie bez sprawy) */
  clientNotice: TaskClientNoticeDto | null;
  /** QA qa10302046 P1: ostatnia blokada z terenu (zadania z terminem) — podpowiedź powodu przełożenia */
  lastBlocker?: { category: string; description: string; resolution: string; resolved: boolean } | null;
}

/** U3: e-mail do klienta o terminie prac. */
export interface TaskClientNoticeDto {
  /** adres e-mail klienta (null — klient bez adresu, wysyłka niemożliwa) */
  email: string | null;
  /** ostatnia informacja: termin albo odwołanie */
  last: { at: string; to: string; term: string; cancelled: boolean } | null;
}

export interface TaskSegmentDto {
  id: string;
  dateFrom: string;
  dateTo: string;
  timeFrom: string | null;
  timeTo: string | null;
  skipWeekends: boolean;
  teamId: string | null;
  added: string[];
  excluded: string[];
}

export interface PlanPreviewDto {
  /** dni wszystkich wpisów; segment — numer wpisu (od 0) */
  days: { day: string; segment: number; people: { userId: string; fullName: string; source: 'ekipa' | 'recznie' }[] }[];
  conflicts: ConflictDto[];
  conditional: boolean;
  /** QA qa10302243 P1: osoby, których zaplanowane przydziały zapis odwoła (od pierwszego odwołanego dnia) */
  dropped: { userId: string; fullName: string; fromDay: string; days: number }[];
  /** Flota (docs/19 §6): auto ekipy / osoby niedostępne w dniach pracy — ostrzeżenie, bez blokady (może być auto zastępcze) */
  fleetWarnings?: string[];
  /** gotowość materiałowa na proponowany termin (null — brak zapotrzebowań albo uprawnień) */
  materials: TaskMaterialsCheckDto | null;
}

export interface CalendarEntryDto {
  id: string;
  kind: EntryKind;
  title: string;
  absenceType: AbsenceType | null;
  absenceStatus: AbsenceStatus | null;
  dateFrom: string;
  dateTo: string;
  timeFrom: string | null;
  timeTo: string | null;
  allUsers: boolean;
  userIds: string[];
  teamIds: string[];
  note: string;
  /** wydarzenie z potwierdzeniem obecności */
  attendanceRequired: boolean;
  createdByName: string | null;
  decidedByName: string | null;
  decisionNote: string;
  leaveKind: LeaveKind | null;
  /** wniosek o nieobecność: przebieg akceptacji (null — wpis bez wniosku) */
  approval: AbsenceApprovalDto | null;
  /** nieobecność: osoba zastępująca i stan zastępstwa (docs/16) */
  deputy: AbsenceDeputyDto | null;
  can: {
    decide: boolean;
    cancel: boolean;
    /** zmiana dat nieobecności (zastępstwo idzie za datami) */
    editDates: boolean;
    /** wskazanie / zmiana / usunięcie osoby zastępującej */
    setDeputy: boolean;
  };
}

/**
 * Zastępstwo przy nieobecności: `czeka` — wniosek jeszcze niezatwierdzony (zastępstwo zacznie obowiązywać po zatwierdzeniu),
 * `zaplanowane` — zatwierdzone, przed okresem, `trwa` — dziś, `zakonczone` — po okresie.
 */
export type AbsenceDeputyState = 'czeka' | 'zaplanowane' | 'trwa' | 'zakonczone';
export interface AbsenceDeputyDto {
  id: string;
  name: string;
  state: AbsenceDeputyState;
  /** e-mail do klientów o osobie kontaktowej wyjdzie po zatwierdzeniu nieobecności */
  notifyPending: boolean;
}

/** Poziom drabinki akceptacji we wniosku (kopia drabinki z chwili złożenia). */
export interface AbsenceApprovalStepDto {
  position: number;
  approverId: string;
  approverName: string;
  decision: ApprovalDecision | null;
  /** kto faktycznie zdecydował (np. zastępca) */
  decidedByName: string | null;
  note: string;
  decidedAt: string | null;
}

export interface AbsenceApprovalDto {
  steps: AbsenceApprovalStepDto[];
  /** wniosek w toku: kto decyduje teraz; null — wniosek rozpatrzony lub wycofany */
  current: {
    /** poziom drabinki; null — brak drabinki albo nikt z niej nie jest dostępny */
    position: number | null;
    names: string[];
    /** drabinka — osoba z poziomu; zastepstwo — zastępca nieobecnej osoby; uprawnienie — osoby z „Zatwierdzanie nieobecności” */
    via: 'drabinka' | 'zastepstwo' | 'uprawnienie';
  } | null;
}

/** Drabinka akceptacji pracownika (karta pracownika). */
export interface AbsenceLadderDto {
  userId: string;
  approvers: { approverId: string; fullName: string; active: boolean }[];
  /** gdy drabinka jest pusta albo nikt z niej nie jest dostępny — osoby z uprawnieniem „Zatwierdzanie nieobecności” */
  fallbackNames: string[];
}

/** Kto wpisał odpowiedź: uczestnik w aplikacji terenowej, uczestnik sam w panelu (np. z dzwonka), operator za uczestnika. */
export type AttendanceSource = 'aplikacja' | 'osobiscie' | 'panel';

/** Obecność na wydarzeniu: uczestnicy (osoby, członkowie ekip, „wszyscy”) i ich potwierdzenia. */
export interface EventAttendanceDto {
  entryId: string;
  title: string;
  attendanceRequired: boolean;
  people: {
    userId: string;
    fullName: string;
    /** true — potwierdził obecność, false — zgłosił nieobecność, null — brak odpowiedzi */
    present: boolean | null;
    source: AttendanceSource | null;
    setByName: string | null;
    setAt: string | null;
    /** ma aktywny telefon z aplikacją */
    hasApp: boolean;
  }[];
  confirmed: number;
  declined: number;
  total: number;
  /** moja odpowiedź — `null`, gdy nie jestem uczestnikiem */
  me: { present: boolean | null } | null;
  /**
   * list — lista obecności (kto będzie, a kto nie) tylko z `kalendarz.wpisy`; bez niej `people` jest puste, a liczniki = 0.
   * set — wpis za innych (operator); self — własna odpowiedź „Będę / Nie będę”.
   */
  can: { list: boolean; set: boolean; self: boolean };
}

/** Element kalendarza osoby w danym dniu (zadanie albo wpis). */
export interface CalendarItemDto {
  userId: string;
  day: string;
  kind: 'zadanie' | EntryKind;
  timeFrom: string | null;
  timeTo: string | null;
  title: string;
  /** zadanie: id zadania; wpis: id wpisu */
  refId: string;
  color: string | null;
  status: string | null;
  /** szczegóły ukryte (zadanie innej ekipy dla użytkownika terenowego) */
  restricted: boolean;
  /** nieobecność: osoba zastępująca (zastępstwo zatwierdzone albo czekające na zatwierdzenie wniosku) */
  deputyName?: string | null;
}

export interface PlanningBoardDto {
  from: string;
  to: string;
  people: PersonDto[];
  teams: (TeamDto & { days: { day: string; userIds: string[] }[] })[];
  tasks: TaskDto[];
  entries: CalendarEntryDto[];
  /** osoby bez ekipy (np. handlowcy z wizjami) */
  unassignedPeople: string[];
  /** Flota (docs/19 §6): auta ekip i osób niedostępne w dniach tablicy (brak — moduł wyłączony albo bez dostępności floty) */
  fleetBlocks?: FleetBlockDto[];
}

export interface PlanningQueueDto {
  /** montaż do zaplanowania — jeden na umowę (wszystkie branże umowy w jednym zadaniu, jeden protokół odbioru) */
  scopes: {
    /** pierwszy zakres (zgodność) — wszystkie: scopeIds */
    scopeId: string;
    scopeIds: string[];
    tradeCodes: string[];
    contractId: string;
    contractNumber: string | null;
    caseId: string;
    caseNumber: string;
    caseTitle: string;
    clientName: string;
    city: string;
    tradeCode: string;
    companyId: string;
    conditionsMet: boolean;
    conditionsText: string;
    /** QA qa10310325 P4: wstrzymana sprawa albo wszystkie zakresy umowy — zostaje w kolejce, ale bez planowania do wznowienia */
    onHold?: { what: 'sprawa' | 'zakres'; reason: string; until: string | null } | null;
  }[];
  tasks: TaskDto[];
  /** zadania w toku / zaplanowane bez dni od dziś — do zaplanowania kolejnych dni (praca do protokołu odbioru) */
  continue: TaskDto[];
}

export interface CalendarDto {
  from: string;
  to: string;
  people: PersonDto[];
  items: CalendarItemDto[];
  entries: CalendarEntryDto[];
}

export interface MembershipChangeDto {
  team: TeamDto;
  /** zadania przeliczone po zmianie składu (dni przyszłe, przydziały „przez ekipę”) z nowymi konfliktami */
  affected: { taskId: string; number: string; title: string; added: string[]; removed: string[]; conflicts: ConflictDto[] }[];
}

export type { TaskStatus };

/** Skutki wstrzymania zakresu / sprawy dla prac (okno potwierdzenia, docs/10 §16). */
export interface HeldWorkDto {
  id: string;
  number: string;
  label: string;
  status: string;
  dateFrom: string | null;
  dateTo: string | null;
  /** termin z ostatniego e-maila do klienta (nieodwołany) — klient zna termin; null/brak — klient nie był powiadomiony */
  clientTerm?: string | null;
}
export interface WorkHoldPreviewDto {
  /** realizacje nierozpoczęte (zaplanowane i w kolejce) — zostaną anulowane; po wznowieniu koordynator tworzy nowe */
  cancelled: HeldWorkDto[];
  /** pozostałe zaplanowane, nierozpoczęte (wizja, pomiary…) — zostaną wycofane z planu do kolejki */
  planned: HeldWorkDto[];
  /** rozpoczęte — wstrzymanie ich nie zmienia (decyzja koordynatora) */
  started: HeldWorkDto[];
}
